import React from 'react';
import { CalendarRange, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';

// Shared "date added" filter for staff lists (Donations, Sponsors, ...).
// value = { preset: 'all'|'this_month'|'last_month'|'this_year'|'last_30'|'custom', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const emptyDateRange = () => ({ preset: 'all', from: '', to: '' });

export function resolveDateRange(value) {
  const now = new Date();
  const y = now.getFullYear(); const m = now.getMonth();
  switch (value?.preset) {
    case 'this_month': return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
    case 'last_month': return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'this_year': return { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)) };
    case 'last_30': { const d = new Date(now); d.setDate(d.getDate() - 30); return { from: iso(d), to: iso(now) }; }
    case 'custom': return { from: value.from || '', to: value.to || '' };
    default: return { from: '', to: '' };
  }
}

/** Returns true when the ISO/Date `when` falls inside the selected range (inclusive, local dates). */
export function inDateRange(when, value) {
  if (!value || value.preset === 'all') return true;
  const { from, to } = resolveDateRange(value);
  if (!from && !to) return true;
  if (!when) return false;
  const d = iso(new Date(when));
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

export function describeDateRange(value) {
  if (!value || value.preset === 'all') return 'All time';
  const { from, to } = resolveDateRange(value);
  const f = (s) => (s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '…');
  return `${f(from)} – ${f(to)}`;
}

export function DateRangeFilter({ value, onChange, label = 'Date added', testId = 'date-range' }) {
  const v = value || emptyDateRange();
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`${testId}-filter`}>
      <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] whitespace-nowrap"><CalendarRange className="w-3.5 h-3.5" /> {label}</div>
      <Select value={v.preset} onValueChange={(preset) => set({ preset })}>
        <SelectTrigger className="h-9 w-full sm:w-40" aria-label={`${label} range`} data-testid={`${testId}-preset`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All time</SelectItem>
          <SelectItem value="this_month">This month</SelectItem>
          <SelectItem value="last_month">Last month</SelectItem>
          <SelectItem value="last_30">Last 30 days</SelectItem>
          <SelectItem value="this_year">This year</SelectItem>
          <SelectItem value="custom">Custom…</SelectItem>
        </SelectContent>
      </Select>
      {v.preset === 'custom' && (
        <div className="flex items-center gap-2">
          <Input type="date" className="h-9 w-36" value={v.from} onChange={(e) => set({ from: e.target.value })} aria-label="From date" data-testid={`${testId}-from`} />
          <span className="text-xs text-[hsl(var(--muted-foreground))]">to</span>
          <Input type="date" className="h-9 w-36" value={v.to} onChange={(e) => set({ to: e.target.value })} aria-label="To date" data-testid={`${testId}-to`} />
        </div>
      )}
      {v.preset !== 'all' && (
        <Button type="button" variant="ghost" size="sm" className="h-9 px-2" onClick={() => onChange(emptyDateRange())} aria-label="Clear date filter" data-testid={`${testId}-clear`}><X className="w-3.5 h-3.5 mr-1" /> Clear</Button>
      )}
    </div>
  );
}

// ---- CSV export of whatever is currently on screen (filtered rows) ----
const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** columns: [{ key, label, format? }] ; rows: array of objects. Downloads a UTF-8 CSV (opens in Excel). */
export function downloadCsv(filename, columns, rows) {
  const head = columns.map(c => csvCell(c.label)).join(',');
  const body = rows.map(r => columns.map(c => csvCell(c.format ? c.format(r) : r[c.key])).join(',')).join('\n');
  const blob = new Blob(['\ufeff' + head + '\n' + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const fmtDateGB = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '');
export const fmtMoney = (v) => Number(v || 0).toFixed(2);
export const rangeSlug = (value) => (value?.preset === 'all' ? 'all-time' : (() => { const r = resolveDateRange(value); return `${r.from || 'start'}_to_${r.to || 'now'}`; })());
